import { timeAgo } from '@/shared/utils/timeAgo';
import type { AccessLog, AccessTodaySummary } from '@yawi/types';
import { UserCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getInitials } from '@yawi/infrastructure';

interface TodayAccessCardProps {
  summary: AccessTodaySummary | null;
  logs: AccessLog[];
  loadingSummary?: boolean;
  loadingLogs?: boolean;
}

export function TodayAccessCard({ summary, logs, loadingSummary, loadingLogs }: TodayAccessCardProps) {
  const navigate = useNavigate();

  const AVATAR_COLORS = ['#7F77DD', '#5B9BD5', '#9B8AE0', '#6FBF73'];

  // Ayer se cuenta hasta la misma hora que hoy, así la comparación es justa a cualquier hora.
  const yesterday = summary?.accessesYesterday;
  const deltaPct = summary && yesterday
    ? Math.round(((summary.accessesToday - yesterday) / yesterday) * 100)
    : null;
  const peakLabel = summary?.peakHourStart
    ? (() => {
        const start = new Date(summary.peakHourStart);
        const end = new Date(start.getTime() + 3_600_000);
        const fmt = (d: Date) => d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
        return `${fmt(start)}–${fmt(end)}`;
      })()
    : null;

  return (
    <div className="bg-white dark:bg-surface-secondary rounded-xl border-[0.5px] border-[#E8E6F8] dark:border-theme-light p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h4 className="text-[15px] font-semibold text-[#1A1A18] dark:text-theme">Accesos del día</h4>
        <button
          onClick={() => navigate('/registro-accesos')}
          className="text-[12px] text-[#7F77DD] dark:text-primary-400 font-medium hover:underline"
        >
          Ver todo
        </button>
      </div>

      {!loadingSummary && summary && (
        <div className="-mt-1 space-y-0.5">
          <p className="text-[12px] text-[#888780] dark:text-theme-tertiary">
            {summary.accessesToday} accesos · {summary.visitsToday} visitas
            {summary.incidentsToday > 0 && (
              <span className="text-[#D64545]"> · {summary.incidentsToday} incidentes</span>
            )}
          </p>
          {(deltaPct !== null || peakLabel) && (
            <p className="text-[12px] text-[#888780] dark:text-theme-tertiary">
              {deltaPct !== null && (
                <span className={deltaPct >= 0 ? 'text-[#3C9F3C] dark:text-green-400' : 'text-[#D64545] dark:text-red-400'}>
                  {deltaPct >= 0 ? '+' : ''}{deltaPct}% vs ayer
                </span>
              )}
              {deltaPct !== null && peakLabel && ' · '}
              {peakLabel && <>Hora pico: {peakLabel}</>}
            </p>
          )}
        </div>
      )}

      <div className="flex flex-col gap-3 flex-1 min-h-0">
        {loadingLogs ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-2.5 animate-pulse">
                <div className="w-8 h-8 rounded-full bg-theme-light/40 flex-shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-2.5 w-2/3 bg-theme-light/40 rounded" />
                  <div className="h-2 w-1/3 bg-theme-light/30 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : logs.length === 0 ? (
          <div className="py-4 text-center">
            <UserCircle size={24} className="mx-auto text-[#888780] dark:text-theme-tertiary mb-1" />
            <p className="text-[13px] text-[#888780] dark:text-theme-tertiary">Sin registros recientes</p>
          </div>
        ) : (
          <div className="space-y-3">
            {logs.slice(0, 5).map((log, i) => (
              <div key={log.id} className="flex items-center gap-2.5">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-medium flex-shrink-0"
                  style={{ backgroundColor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}
                >
                  {log.userName ? getInitials(log.userName) : '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-[#1A1A18] dark:text-theme truncate leading-tight">
                    {log.userName ?? 'Desconocido'}
                  </p>
                  <p className="text-[11px] text-[#888780] dark:text-theme-tertiary leading-tight truncate">
                    {log.entryPoint ?? 'Acceso'}
                  </p>
                </div>
                <span className="text-[11px] text-[#888780] dark:text-theme-tertiary flex-shrink-0 whitespace-nowrap">
                  {timeAgo(log.timestamp)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
